// FinePartita.tsx — v033: due pezzi del pannello di fine partita. Il riepilogo (quanto si è resistito, il round, la
// serie) e «Aggiungi agli amici» per l'altro giocatore, se tutti e due avete l'account (lo dice il server).
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { amiciziaPartita, chiediAmiciziaPartita, type StatoAmicizia } from "../servizi/amici.ts";
import { PulsanteAmicizia } from "../social/PulsanteAmicizia.tsx";
import { altro, type Ruolo, type VistaPartita } from "../../../condiviso/partita.ts";
import { Finestra } from "../componenti/Finestra.tsx";
import { condividi, vibra } from "../servizi/nativo.ts";
import { nomeFileClip, type StatoClip } from "./usaClip.ts";
import { IMP_APP, MARCHIO, PERCORSO_CLASSIFICA, PERCORSO_NEGOZIO } from "../costanti.ts";
import { useAccount } from "../servizi/account.ts";
import { prossimoObiettivo, type Obiettivo } from "../servizi/obiettivo.ts";
import { vai } from "../servizi/percorso.ts";
import { eBooleano, leggiImpostazione } from "../servizi/impostazioni.ts";
import { cartaMeme } from "./cartaMeme.ts";
import { componiFoto, type FotoPartita } from "./fotoPartita.ts";
import { noiPossibileQui, salvaScelte, scelteAttuali, useScelte } from "../servizi/scelte.ts";
import { InterruttoriScelte } from "../componenti/InterruttoriScelte.tsx";

const mmss = (ms: number) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

export function Riepilogo({ v, io }: { v: VistaPartita; io: Ruolo }) {
  const { t } = useTranslation();
  const lui = altro(io);
  const voci: [string, string, string][] = [
    ["⏱", mmss(v.tempoGiocatoMs), t("fine.resistito")],
    ["🎬", String(v.round), t("fine.round")],
  ];
  if (v.serie.tipo === "meglio3") voci.push(["🏆", `${v.serie.vittorie[io]}–${v.serie.vittorie[lui]}`, t("fine.serie")]);
  return (
    <dl className="ld-riepilogo" aria-label={t("fine.riepilogo")}>
      {voci.map(([icona, valore, etichetta], i) => (
        <div key={etichetta} className="ld-riepilogo-voce" style={{ animationDelay: `${120 + i * 90}ms` }}>
          <dt>
            <span aria-hidden="true">{icona}</span> {etichetta}
          </dt>
          <dd>{valore}</dd>
        </div>
      ))}
    </dl>
  );
}

export function AmiciziaFinePartita({ codice, nome }: { codice: string; nome: string }) {
  const [stato, setStato] = useState<StatoAmicizia | null>(null);
  const [giaAmici, setGiaAmici] = useState(false);
  useEffect(() => {
    let vivo = true;
    amiciziaPartita(codice)
      .then((r) => {
        if (!vivo) return;
        if (r.stato === "amici") setGiaAmici(true);
        else if (r.stato === "nessuna" || r.stato === "inviata" || r.stato === "ricevuta") setStato(r.stato);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [codice]);
  if (giaAmici || !stato) return null;
  return <PulsanteAmicizia stato={stato} chiedi={() => chiediAmiciziaPartita(codice)} nome={nome} />;
}

// --- v035: la finestra «Che partita!» -------------------------------------------------------------------------------
// v040: ripensata (il proprietario: «troppi pulsanti, l'utente non sa quale premere»). Tre cose sole, una sotto l'altra:
//   🎬 Condividi il video   — il video delle risate, con il link per sfidarmi
//   📸 Le foto della partita — alcune foto prese durante la partita, nella cornice di Laughdown: scegli quali
//   💬 Fatti sfidare         — un messaggio con il tuo link (dal foglio del telefono: WhatsApp, Instagram, SMS…)
// Niente più «WhatsApp» e «Condividi» quasi uguali, niente «Scarica» ed «Esci» doppi.
// v045 (virale): in cima una frase a effetto e «perché non la condividi?»; il video va sempre con il link per sfidarmi
// (niente più video online); «ti mancano X ⭐ per …», il Wall of Fame e, in fondo, «Gioca ancora» (rivincita o un
// altro amico): una partita dietro l'altra.

/** Prova a condividere dei file (video o foto) dal foglio del telefono (Instagram, TikTok, WhatsApp…). */
async function condividiFile(file: File[], testo: string): Promise<"fatto" | "chiuso" | "no"> {
  try {
    if (file.length && navigator.canShare?.({ files: file })) {
      await navigator.share({ files: file, text: testo, title: MARCHIO });
      return "fatto";
    }
  } catch (e) {
    return (e as Error)?.name === "AbortError" ? "chiuso" : "no"; // chiuso il foglio: va bene così
  }
  return "no";
}

/** Scarica un file sul telefono (quando il foglio di condivisione non accetta i file). */
function scaricaBlob(blob: Blob, nome: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/** WhatsApp con il testo e il link già scritti (sul telefono apre l'app, sul computer WhatsApp Web). */
export function linkWhatsapp(testo: string): string {
  return `https://wa.me/?text=${encodeURIComponent(testo)}`;
}

type Vista = "menu" | "foto" | "scelte";

/** v045: le frasi a effetto in cima (una a caso, diversa a ogni partita). */
const FRASI = { vinto: 4, perso: 4, pari: 2 } as const;

export function FinestraFinePartita(p: {
  aperta: boolean;
  onChiudi: () => void;
  vinto: boolean | null;
  nomeLui: string;
  clip: { stato: StatoClip; crea: () => void; consenso: boolean; consensoAltro: boolean; disponibile: boolean };
  /** v040: le foto della partita (vuoto = niente foto) */
  foto: FotoPartita[];
  /** v040: l'altro ha detto sì alle foto */
  fotoConLui: boolean;
  /** link per sfidarmi (link personale se c'è, altrimenti il sito): v045 è sempre questo il link che si manda */
  link: string;
  lingua?: string;
  /** stelle: quante per ogni amico che arriva (null = crediti spenti o ospite) */
  premi?: { condivisione: number; amico: number } | null;
  /** nel torneo il passo dopo è tornare al tabellone: un pulsante solo, in fondo */
  onTorneo?: (() => void) | null;
  /** v045: «Sfida un altro amico» (una sfida nuova) */
  onNuovaSfida?: (() => void) | null;
  /** v045: «Rivincita» subito (senza: si chiude e sotto c'è il pannello) */
  onRivincita?: (() => void) | null;
}) {
  const { t } = useTranslation();
  const account = useAccount();
  const io = account.tipo === "registrato" ? account.io : null;
  const { stato, crea, consenso, disponibile } = p.clip;
  const [vista, setVista] = useState<Vista>("menu");
  const [avviso, setAvviso] = useState<string | null>(null);
  // il video si prepara da solo appena si apre la finestra
  useEffect(() => {
    if (p.aperta && stato.s === "pronta_da_creare") crea();
  }, [p.aperta, stato.s, crea]);
  useEffect(() => {
    if (!p.aperta) setVista("menu");
  }, [p.aperta]);
  const esito = p.vinto === true ? "vinto" : p.vinto === false ? "perso" : "pari";
  // una frase a caso, scelta a ogni apertura (non a ogni ridisegno)
  const fraseVideo = useMemo(() => `fine.video_geniale_${1 + Math.floor(Math.random() * 3)}`, [p.aperta]); // eslint-disable-line react-hooks/exhaustive-deps
  const sceltePerNoi = useScelte();
  const noiPossibile = noiPossibileQui();
  const frase = useMemo(() => `fine.frase_${esito}_${1 + Math.floor(Math.random() * FRASI[esito])}`, [esito, p.aperta]); // eslint-disable-line react-hooks/exhaustive-deps
  // v045: «ti mancano X ⭐ per …»: un traguardo vicino, per giocare ancora
  const [obiettivo, setObiettivo] = useState<Obiettivo | null>(null);
  const crediti = io?.crediti ?? null;
  useEffect(() => {
    if (!p.aperta || crediti === null || !p.premi) return;
    let vivo = true;
    void prossimoObiettivo(crediti, p.lingua ?? "en")
      .then((o) => vivo && setObiettivo(o))
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [p.aperta, crediti, p.premi, p.lingua]);
  const testo = t(`fine.social_${esito}`, { nome: p.nomeLui });
  const avvisa = (s: string) => {
    setAvviso(s);
    setTimeout(() => setAvviso((x) => (x === s ? null : x)), 2500);
  };
  const copia = async (s: string) => {
    try {
      await navigator.clipboard.writeText(s);
      avvisa(t("fine.copiato_testo"));
    } catch {
      // niente appunti: pazienza
    }
  };

  // v045: il video va sempre con il link per sfidarmi («Riesci a non ridere? Sfidami»): niente più video online
  const condividiVideo = async () => {
    if (stato.s !== "fatta") return;
    vibra("medio");
    const file = new File([stato.clip.blob], nomeFileClip(stato.clip.tipo), { type: stato.clip.tipo.split(";")[0] });
    const r = await condividiFile([file], `${t("fine.social_video")} ${p.link} #laughdown #nonridere`);
    if (r !== "no") return;
    // il browser non condivide i file (computer): si scarica il video
    scaricaBlob(stato.clip.blob, nomeFileClip(stato.clip.tipo));
    avvisa(t("fine.scaricato"));
  };
  const fattiSfidare = async () => {
    vibra("leggero");
    const testoSfida = `${t("fine.amico_testo")} ${p.link}`;
    if (await condividi({ titolo: MARCHIO, testo: t("fine.amico_testo"), url: p.link })) return;
    await copia(testoSfida);
  };
  const videoInArrivo = stato.s === "creazione" || stato.s === "pronta_da_creare" || stato.s === "registra";
  const titolo = t(`fine.titolo_${esito}`);
  const anteprima = p.foto.find((f) => f.momento === "risata") ?? p.foto[p.foto.length - 1] ?? null;
  const qualcosaDaCondividere = (disponibile && (stato.s === "fatta" || (videoInArrivo && consenso))) || p.foto.length > 0;

  return (
    <Finestra
      schermoIntero
      aperta={p.aperta}
      titolo={vista === "foto" ? t("fine.foto_titolo") : vista === "scelte" ? t("scelte.titolo") : titolo}
      onChiudi={vista === "menu" ? p.onChiudi : () => setVista("menu")}
    >
      {vista === "scelte" ? (
        // v042: le scelte su foto e video si cambiano da qui (prima c'era la riga «Cambia» prima di ogni partita)
        <div className="ld-scelte-finestra">
          <p className="nota">{t("scelte.intro")}</p>
          <InterruttoriScelte />
          <p className="nota">{t("scelte.partita_nota", { nome: p.nomeLui })}</p>
          <button type="button" className="pulsante pulsante-secondario" onClick={() => setVista("menu")}>
            {t("generale.chiudi")}
          </button>
        </div>
      ) : vista === "foto" ? (
        <GalleriaFoto foto={p.foto} conLui={p.fotoConLui} vinto={p.vinto} nomeLui={p.nomeLui} link={p.link} testo={testo} onFatto={() => setVista("menu")} />
      ) : (
        <div className="ld-fine">
          {/* v045: la frase a effetto e l'invito a condividere */}
          <p className="ld-fine-frase" data-esito={esito} key={frase}>
            {t(frase, { nome: p.nomeLui })}
          </p>
          {stato.s === "fatta" ? (
            <video src={stato.url} controls playsInline autoPlay muted loop className="ld-fine-video" />
          ) : anteprima?.io || anteprima?.lui ? (
            <figure className="ld-fine-polaroid">
              <img src={(anteprima.io ?? anteprima.lui)!} alt="" />
            </figure>
          ) : null}
          {/* v046: con il video pronto, una frase che lo fa sembrare (ed è) da condividere subito */}
          <p className="ld-fine-invito" data-video={stato.s === "fatta" || undefined}>
            {stato.s === "fatta" ? t(fraseVideo) : qualcosaDaCondividere ? t("fine.invito_condividi") : t("fine.invito_sfida")}
          </p>
          {stato.s === "fatta" && noiPossibile && !sceltePerNoi.noi && (
            <button type="button" className="pulsante-link ld-fine-noi" onClick={() => setVista("scelte")}>
              {t("fine.video_a_noi")}
            </button>
          )}

          <div className="ld-fine-tre" role="group" aria-label={t("fine.cosa_condividi")}>
            {/* 🎬 il video */}
            {disponibile && stato.s === "fatta" && (
              <button type="button" className="ld-fine-azione ld-fine-azione-video ld-importante" onClick={() => void condividiVideo()}>
                <span className="ld-fine-azione-icona" aria-hidden="true">🎬</span>
                <span className="ld-fine-azione-testo">
                  <strong>{t("fine.condividi_video_breve")}</strong>
                  <span>{t("fine.video_con_sfida")}</span>
                </span>
              </button>
            )}
            {disponibile && videoInArrivo && consenso && (
              <p className="ld-fine-azione ld-fine-azione-attesa" role="status" aria-live="polite">
                <span className="ld-fine-azione-icona" aria-hidden="true">🎬</span>
                <span className="ld-fine-azione-testo">
                  <strong>{t("fine.preparo_video", { p: stato.s === "creazione" ? Math.round(stato.avanzamento * 100) : 0 })}</strong>
                </span>
              </p>
            )}
            {/* 📸 le foto */}
            {p.foto.length > 0 && (
              <button type="button" className="ld-fine-azione ld-fine-azione-foto" onClick={() => setVista("foto")}>
                <span className="ld-fine-azione-icona" aria-hidden="true">📸</span>
                <span className="ld-fine-azione-testo">
                  <strong>{t("fine.foto_partita", { n: p.foto.length })}</strong>
                  <span>{p.fotoConLui ? t("fine.foto_nota") : t("fine.foto_nota_solo", { nome: p.nomeLui })}</span>
                </span>
              </button>
            )}
            {/* 💬 il messaggio per farsi sfidare */}
            <button type="button" className={`ld-fine-azione ld-fine-azione-sfida${qualcosaDaCondividere ? "" : " ld-importante"}`} onClick={() => void fattiSfidare()}>
              <span className="ld-fine-azione-icona" aria-hidden="true">💬</span>
              <span className="ld-fine-azione-testo">
                <strong>{t("fine.fatti_sfidare")}</strong>
                <span>{t("fine.fatti_sfidare_nota")}</span>
              </span>
            </button>
          </div>

          {/* le stelle per ogni amico che arriva dal link */}
          {p.premi && p.premi.amico > 0 && <p className="ld-fine-premi">{t("fine.premi", { amico: p.premi.amico })}</p>}
          {avviso && (
            <p className="nota-centrata" role="status">
              {avviso}
            </p>
          )}

          {/* v045: il traguardo vicino (ti mancano X ⭐) e il Wall of Fame */}
          {obiettivo && (
            <button type="button" className="ld-fine-obiettivo" onClick={() => vai(PERCORSO_NEGOZIO)}>
              <span className="ld-fine-obiettivo-testo">
                {obiettivo.mancano > 0 ? t("fine.obiettivo", { n: obiettivo.mancano, nome: obiettivo.nome }) : t("fine.obiettivo_pronto", { nome: obiettivo.nome })}
              </span>
              <span className="barra-avanzamento" role="progressbar" aria-valuemin={0} aria-valuemax={obiettivo.prezzo} aria-valuenow={obiettivo.prezzo - obiettivo.mancano}>
                <span style={{ inlineSize: `${Math.round(((obiettivo.prezzo - obiettivo.mancano) / obiettivo.prezzo) * 100)}%` }} />
              </span>
            </button>
          )}
          {io?.inClassifica && leggiImpostazione(IMP_APP.classificaAttiva, true, eBooleano) && (
            <button type="button" className="pulsante-link ld-fine-wall" onClick={() => vai(PERCORSO_CLASSIFICA)}>
              {p.vinto ? t("fine.wall_vinto") : t("fine.wall_vedi")}
            </button>
          )}

          {/* niente video o niente foto perché non li ho scelti: si accendono qui per la prossima volta, un tocco */}
          <SpentiPerScelta video={disponibile && !consenso && stato.s !== "fatta"} foto={p.foto.length === 0} />

          {/* v045: una partita dietro l'altra */}
          {p.onTorneo ? (
            <button type="button" className="pulsante pulsante-primario pulsante-grande" onClick={p.onTorneo}>
              {t("torneo.torna")}
            </button>
          ) : (
            <div className="ld-fine-ancora">
              <p className="ld-fine-ancora-titolo">{t("fine.ancora_titolo")}</p>
              <button type="button" className="pulsante pulsante-primario pulsante-grande" onClick={p.onRivincita ?? p.onChiudi}>
                🔁 {p.onRivincita ? t("fine.ancora_rivincita", { nome: p.nomeLui }) : t("fine.ancora_gioca")}
              </button>
              {p.onNuovaSfida && (
                <button type="button" className="pulsante pulsante-secondario pulsante-grande" onClick={p.onNuovaSfida}>
                  👥 {t("fine.ancora_altro")}
                </button>
              )}
            </div>
          )}
          <button type="button" className="pulsante-link ld-fine-scelte" onClick={() => setVista("scelte")}>
            {t("fine.cambia_scelte")}
          </button>
        </div>
      )}
    </Finestra>
  );
}

/** v040: «Niente video/foto: li vuoi dalla prossima partita?» (solo se è stata una mia scelta). */
function SpentiPerScelta({ video, foto }: { video: boolean; foto: boolean }) {
  const { t } = useTranslation();
  const [fatto, setFatto] = useState(false);
  const sc = scelteAttuali();
  const manca = { video: video && !sc.video, foto: foto && !sc.foto };
  if (!manca.video && !manca.foto) return fatto ? <p className="nota-centrata" role="status">{t("fine.dalla_prossima")}</p> : null;
  return (
    <div className="ld-fine-video-no">
      <p className="nota">{manca.video && manca.foto ? t("fine.spenti_tutti") : manca.video ? t("fine.video_spento") : t("fine.foto_spente")}</p>
      <button
        type="button"
        className="pulsante pulsante-secondario"
        onClick={() => {
          void salvaScelte({ ...sc, video: sc.video || manca.video, foto: sc.foto || manca.foto }).catch(() => undefined);
          setFatto(true);
        }}
      >
        {t("fine.accendi_prossima")}
      </button>
    </div>
  );
}

/** v040: le foto della partita nella cornice di Laughdown; si toccano per sceglierle, poi «Condividi». */
function GalleriaFoto(p: { foto: FotoPartita[]; conLui: boolean; vinto: boolean | null; nomeLui: string; link: string; testo: string; onFatto: () => void }) {
  const { t } = useTranslation();
  // per ogni foto: con tutti e due (se l'altro ha detto sì) e una solo mia, quella della risata
  const voci = useMemo(() => {
    const out: { id: string; foto: FotoPartita; conLui: boolean }[] = [];
    for (const f of p.foto) {
      if (p.conLui && f.io && (f.lui || f.griglia)) out.push({ id: `${f.id}-due`, foto: f, conLui: true });
      else if (f.io) out.push({ id: `${f.id}-io`, foto: f, conLui: false });
    }
    const risata = p.foto.find((f) => f.momento === "risata");
    if (p.conLui && risata?.io && (risata.lui || risata.griglia)) out.push({ id: `${risata.id}-io`, foto: risata, conLui: false });
    return out;
  }, [p.foto, p.conLui]);
  const chiaveVoci = voci.map((v) => v.id).join("|");
  const [carte, setCarte] = useState<Record<string, { blob: Blob; url: string }>>({});
  const [scelte, setScelte] = useState<Set<string>>(() => new Set(voci.map((v) => v.id)));
  const [lavoro, setLavoro] = useState(false);
  const [nota, setNota] = useState<string | null>(null);
  const urlRif = useRef<string[]>([]);
  const urlFatte = useRef(new Set<string>());
  useEffect(() => {
    let vivo = true;
    const titolo = p.vinto === true ? t("fine.storia_vinto", { nome: p.nomeLui }) : p.vinto === false ? t("fine.storia_perso", { nome: p.nomeLui }) : t("fine.storia_pari", { nome: p.nomeLui });
    const carattere = getComputedStyle(document.documentElement).getPropertyValue("--ld-f-marchio").trim();
    void (async () => {
      for (const v of voci) {
        if (urlFatte.current.has(v.id)) continue;
        const foto = await componiFoto(v.foto, v.conLui);
        if (!vivo || !foto) continue;
        const sotto = v.conLui ? t("fine.foto_noi", { nome: p.nomeLui }) : t("fine.storia_sotto");
        const blob = await cartaMeme({ foto, titolo, sotto, link: p.link, carattere });
        if (!vivo || !blob) continue;
        const url = URL.createObjectURL(blob);
        urlRif.current.push(url);
        urlFatte.current.add(v.id);
        setCarte((c) => ({ ...c, [v.id]: { blob, url } }));
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chiaveVoci]);
  useEffect(() => () => urlRif.current.forEach((u) => URL.revokeObjectURL(u)), []);
  const pronte = voci.filter((v) => carte[v.id]);
  const scelteOra = pronte.filter((v) => scelte.has(v.id));

  const condividiScelte = async () => {
    if (!scelteOra.length) {
      setNota(t("fine.foto_scegli"));
      return;
    }
    setLavoro(true);
    vibra("medio");
    const file = scelteOra.map((v, i) => new File([carte[v.id]!.blob], `laughdown-${i + 1}.jpg`, { type: "image/jpeg" }));
    const r = await condividiFile(file, `${p.testo} ${p.link} #laughdown`);
    if (r === "no") {
      scelteOra.forEach((v, i) => scaricaBlob(carte[v.id]!.blob, `laughdown-${i + 1}.jpg`));
      setNota(t("fine.scaricate", { n: scelteOra.length }));
    } else if (r === "fatto") p.onFatto();
    setLavoro(false);
  };

  return (
    <div className="ld-galleria">
      <p className="nota-centrata">{t("fine.foto_tocca")}</p>
      <ul className="ld-galleria-griglia">
        {voci.map((v) => {
          const c = carte[v.id];
          const scelta = scelte.has(v.id);
          return (
            <li key={v.id}>
              {!c ? (
                <span className="ld-galleria-foto" aria-hidden="true">
                  <span className="ld-galleria-attesa" />
                </span>
              ) : (
                <button
                  type="button"
                  className="ld-galleria-foto"
                  aria-pressed={scelta}
                  aria-label={v.conLui ? t("fine.foto_noi", { nome: p.nomeLui }) : t("fine.foto_io")}
                  onClick={() =>
                    setScelte((s) => {
                      const n = new Set(s);
                      if (n.has(v.id)) n.delete(v.id);
                      else n.add(v.id);
                      return n;
                    })
                  }
                >
                  <img src={c.url} alt="" />
                  <span className="ld-galleria-spunta" aria-hidden="true">{scelta ? "✓" : ""}</span>
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {nota && (
        <p className="nota-centrata" role="status">
          {nota}
        </p>
      )}
      {pronte.length < voci.length ? (
        <p className="nota-centrata" role="status" aria-live="polite">
          {t("fine.foto_preparo")}
        </p>
      ) : (
        <button type="button" className="ld-cta ld-cta-centrata" disabled={lavoro} onClick={() => void condividiScelte()}>
          {t("fine.foto_condividi", { n: scelteOra.length })}
        </button>
      )}
    </div>
  );
}
